import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';



const Email_Template = () => {
  const [activeTab, setActiveTab] = useState("Client Email Template");
  const [expandedSections, setExpandedSections] = useState({});
   const [loading, setLoading] = useState(false);


  const [enabledSections, setEnabledSections] = useState({});
  const [textareaContent, setTextareaContent] = useState({});
  const [previewContent, setPreviewContent] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("")
  const [defaultTemplateUsed, setDefaultTemplateUsed] = useState({});
  const [fetchedTemplates, setFetchedTemplates] = useState({
    C: [], 
    A: [], 
    S: []  
  });
  const [defaultTemplate, setDefaultTemplate] = useState("");

  const previewRef = useRef(null);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.scrollTop = 0;
    }
  }, [previewContent]);

  useEffect(() => {
    fetchAllTemplates();
  }, []);

  const fetchAllTemplates = async () => {
    setLoading(true);

    try {
      const userTypes = ['C', 'A', 'S'];
      const allTemplates = {};
      const initialEnabledSections = {};
  
      for (const userType of userTypes) {
        const response = await axios.post(`${BaseUrl}/backend/api/email_template.php`, {
          action: 'get_all_email_templates',
          api_key: '1',
          user_type: userType,
        });
  
        if (response.data.status === 'true') {
          const decodedData = response.data.data.map((template) => ({
            ...template,
            email_message: atob(template.email_message),
            default_message: atob(template.default_message),
          }));
  
          allTemplates[userType] = decodedData;
  
          decodedData.forEach((template) => {
            initialEnabledSections[template.email_subject] = template.email_template_status === 'E';
          });
  
          if (response.data.default_template) {
            setDefaultTemplate(atob(response.data.default_template));
          }
        } else {
          console.error(`Failed to fetch templates for user type ${userType}:`, response.data.response);
          toast.error(`Failed to fetch templates for ${userType === 'C' ? 'Client' : userType === 'A' ? 'Admin' : 'Staff'}`);
        }
      }
  
      setFetchedTemplates(allTemplates);
      setEnabledSections(initialEnabledSections);
      initializeTemplateContent(allTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const initializeTemplateContent = (templates) => {
    const initialContent = {};
    Object.entries(templates).forEach(([userType, templateList]) => {
      templateList.forEach((template) => {
        initialContent[template.email_subject] = template.email_message || template.default_message || defaultTemplate;
      });
    });
    setTextareaContent(initialContent);
  };

  const placeholders = [
    "booking_date", "booking_time", "service_name", "client_name", "method_name", 
    "units", "addons", "firstname", "lastname", "client_email", "client_phone", 
    "payment_method", "vaccum_cleaner_status", "parking_status", "notes", 
    "contact_status", "price", "client_address", "client_city", "client_state", 
    "client_zip", "client_promocode", "app_remain_time", "reject_status", 
    "reject_reason", "business_logo", "admin_name", "company_name", "company_address", 
    "company_city", "company_state", "company_zip", "company_country", "company_phone", 
    "company_email"
  ];

  const handleTextClick = (section, text) => {
    const newText = `[[${text}]]\n${textareaContent[section] || ""}`;
    setTextareaContent((prev) => ({
      ...prev,
      [section]: newText,
    }));
    setPreviewContent(newText);
  };

  const handleDefaultTemplate = (section) => {
    const defaultPreviewContent = `
    <div class="font-sans bg-white border border-gray-300 shadow-lg rounded-md max-w-lg mx-auto">
      <div class="p-6">
        <!-- Company Details -->
        <div class="text-gray-700 text-sm mb-4">
          <p>[[company_name]]</p>
          <p>[[company_address]]</p>
          <p>[[company_city]], [[company_state]], [[company_zip]]</p>
          <p>[[company_country]]</p>
          <p>[[company_phone]]</p>
          <p>[[company_email]]</p>
        </div>
        
        <!-- Greeting -->
        <div class="mb-4">
          <p class="text-gray-900 font-semibold">Dear [[client_name]],</p>
          <p class="text-gray-600">You've set a new appointment with the following details:</p>
        </div>

        <!-- Appointment Details -->
        <div class="bg-gray-50 border border-gray-200 rounded-md p-4">
          <p class="text-sm mb-2"><strong>When:</strong> [[booking_date]]</p>
          <p class="text-sm mb-2"><strong>For:</strong> [[service_name]]</p>
          <p class="text-sm mb-2"><strong>Methods:</strong> [[methodname]]</p>
          <p class="text-sm mb-2"><strong>Units:</strong> [[units]]</p>
          <p class="text-sm mb-2"><strong>Add-ons:</strong> [[addons]]</p>
          <p class="text-sm mb-2"><strong>Price:</strong> [[price]]</p>
          <p class="text-sm mb-2"><strong>Name:</strong> [[firstname]] [[lastname]]</p>
          <p class="text-sm mb-2"><strong>Email:</strong> [[client_email]]</p>
          <p class="text-sm mb-2"><strong>Phone:</strong> [[phone]]</p>
          <p class="text-sm mb-2"><strong>Payment:</strong> [[payment_method]]</p>
          <p class="text-sm mb-2"><strong>Vacuum Cleaner:</strong> [[vaccum_cleaner_status]]</p>
          <p class="text-sm mb-2"><strong>Parking:</strong> [[parking_status]]</p>
          <p class="text-sm mb-2"><strong>Address:</strong> [[address]]</p>
          <p class="text-sm mb-2"><strong>Notes:</strong> [[notes]]</p>
          <p class="text-sm mb-2"><strong>Contact Status:</strong> [[contact_status]]</p>
        </div>

        <!-- Appointment Status -->
        <p class="text-gray-500 text-sm italic mt-4">
          Your appointment is tentative, and you will be notified as we confirm this booking.
        </p>

        <!-- Footer -->
        <div class="mt-6 bg-gray-50 text-center py-3 rounded-md">
          <p class="text-gray-900 font-medium">Thank you</p>
        </div>
      </div>
    </div>
  `;
    setTextareaContent((prev) => ({
      ...prev,
      [section]: defaultPreviewContent,
    }));
    setPreviewContent(defaultPreviewContent);
    setDefaultTemplateUsed((prev) => ({
      ...prev,
      [section]: true,
    }));
  };

  const handleSaveTemplate = async (section) => {
    setLoading(true);

    try {
      const template = fetchedTemplates[getUserType(activeTab)].find(t => t.email_subject === section);
      if (!template) {
        console.error(`Template not found for section: ${section}`);
        toast.error(`Template not found for section: ${section}`);
        return;
      }

      const response = await axios.post(`${BaseUrl}/backend/api/email_template.php`, {
        action: 'update_email_template_message',
        api_key: '1',
        id: template.id,
        email_message: btoa(textareaContent[section])
      });

      if (response.data.status === 'true') {
        toast.success(`Template for "${section}" has been saved successfully.`);
        setFetchedTemplates(prev => ({
          ...prev,
          [getUserType(activeTab)]: prev[getUserType(activeTab)].map(t => 
            t.email_subject === section ? {...t, email_message: textareaContent[section]} : t
          )
        }));
      } else {
        toast.error(`Failed to save template: ${response.data.response}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('An error occurred while saving the template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (section) => {
    const modifiedContent = textareaContent[section] || defaultTemplate;
    setPreviewContent(modifiedContent);
    setPreviewTitle(section);
    setIsPreviewOpen(true);
  };

  const toggleEnable = async (section, id) => {
    setLoading(true);

    try {
      const newStatus = !enabledSections[section];
      const response = await axios.post(`${BaseUrl}/backend/api/email_template.php`, {
        action: "update_email_template_status",
        api_key: "1",
        id: id,
        email_template_status: newStatus ? "E" : "D"
      });

      if (response.data.status === "true") {
        setSuccessMessage("Email Template Update Successfully!")
        setEnabledSections((prev) => ({
          ...prev,
          [section]: newStatus,
        }));
        toast.success(`${section} template ${newStatus ? 'enabled' : 'disabled'} successfully`);
      } else {
        console.error("Failed to update email template status:", response.data.response);
        toast.error(`Failed to update ${section} template status`);
      }
    } catch (error) {
      console.error("Error updating email template status:", error);
      toast.error(`Error updating ${section} template status`);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (section) => {
    setExpandedSections((prev) => {
      const newExpandedSections = {};
      Object.keys(prev).forEach(key => {
        newExpandedSections[key] = false;
      });
      newExpandedSections[section] = !prev[section];
      return newExpandedSections;
    });
  };

  const getUserType = (tab) => {
    switch (tab) {
      case "Client Email Template":
        return 'C';
      case "Admin Email Template":
        return 'A';
      case "Staff Email Template":
        return 'S';
      default:
        return 'C';
    }
  };

  const renderSections = (userType) => {
    const templates = fetchedTemplates[userType] || [];
    return templates.map((template) => {
      const section = template.email_subject;
      const templateContent = textareaContent[section] || template.email_message || template.default_message || defaultTemplate;

      return (
        <div key={section} className="mb-6">
     {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
          <div className="flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg shadow-sm border border-gray-200 xl:px-24">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => toggleEnable(section, template.id)}
                className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                  enabledSections[section] ? "bg-blue-500" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all duration-300 ${
                    enabledSections[section] ? "right-1" : "left-1"
                  }`}
                />
              </button>
              <span className="font-semibold text-lg text-gray-800 break-words">
                {section}
              </span>
            </div>

            <button
              onClick={() => toggleExpand(section)}
              className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-all duration-200"
            >
              {expandedSections[section] ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          <div
            className={`transform transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections[section]
                ? "max-h-[800px] opacity-100 mt-4"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <textarea
                    className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none font-mono text-sm"
                    placeholder="Enter your template here..."
                    value={templateContent}
                    onChange={(e) => {
                      setTextareaContent((prev) => ({
                        ...prev,
                        [section]: e.target.value,
                      }));
                      setPreviewContent(e.target.value);
                    }}
                  />
                </div>

                <div className="lg:w-64">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-700 mb-3">Placeholders</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {placeholders.map((text) => (
                        <div
                          key={text}
                          onClick={() => handleTextClick(section, text)}
                          className="text-sm text-gray-600 p-2 rounded-md transition-colors duration-200 cursor-pointer hover:bg-blue-50"
                        >
                          [[{text}]]
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <button
                  onClick={() => handleSaveTemplate(section)}
                  className="px-6 py-2 rounded-lg shadow-sm transition-colors duration-200 bg-blue-500 text-white hover:bg-blue-600"
                >
                  Save Template
                </button>
                <button
                  onClick={() => handleDefaultTemplate(section)}
                  className="px-6 py-2 rounded-lg shadow-sm transition-colors duration-200 bg-gray-500 text-white hover:bg-gray-600"
                >
                  Default Template
                </button>
                <button
                  onClick={() => handlePreview(section)}
                  className="px-6 py-2 rounded-lg shadow-sm transition-colors duration-200 bg-green-500 text-white hover:bg-green-600"
                >
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  const renderPreview = () => {
    return (
      <div dangerouslySetInnerHTML={{ __html: previewContent }} />
    );
  };

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <div className="flex flex-nowrap justify-between border-b border-gray-300 pb-4 mb-8 overflow-x-auto">
        <div className="flex flex-row w-full gap-2">
          {["Client Email Template", "Admin Email Template", "Staff Email Template"].map((tab) => (
            <button
              key={tab}
              className={`flex-1 text-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab
                  ? "bg-blue-500 text-white shadow-md transform -translate-y-0.5"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {renderSections(getUserType(activeTab))}
      </div>

      {isPreviewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">{previewTitle} Preview</h2>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div ref={previewRef} className="p-6 overflow-y-auto h-[calc(90vh-88px)]">
              {renderPreview()}
            </div>
          </div>
        </div>
      )}
    </div>
    
  </div>
  );
};

export default Email_Template;




// const Email_Template = () => {
//   const [activeTab, setActiveTab] = useState("Client Email Template");
//   const [expandedSections, setExpandedSections] = useState({});
//   const [textareaContent, setTextareaContent] = useState({});
//   const [previewContent, setPreviewContent] = useState("");
//   const [isPreviewOpen, setIsPreviewOpen] = useState(false);
//   const [previewTitle, setPreviewTitle] = useState("");
//   const [defaultTemplateUsed, setDefaultTemplateUsed] = useState(false);

//   const defaultPreviewContent = `
//     <div style="font-family: Arial, sans-serif;">
//       <p>[[company_name]]</p>
//       <p>[[company_address]]</p>
//       <p>[[company_city]], [[company_state]],</p>
//       <p>[[company_zip]]</p>
//       <p>[[company_country]]</p>
//       <p>[[company_phone]]</p>
//       <p>[[company_email]]</p>
//       <br/>
//       <p>Dear [[client_name]],</p>
//       <p>You've set a new appointment with following details:</p>
//       <br/>
//       <p>When: [[booking_date]]</p>
//       <p>For: [[service_name]]</p>
//       <p>Methods : [[methodname]]</p>
//       <p>Units : [[units]]</p>
//       <p>Add-ons : [[addons]]</p>
//       <p>Price : [[price]]</p>
//       <p>Name : [[firstname]] [[lastname]]</p>
//       <p>Email : [[client_email]]</p>
//       <p>Phone : [[phone]]</p>
//       <p>Payment : [[payment_method]]</p>
//       <p>Vaccum Cleaner : </p>
//       <p>[[vaccum_cleaner_status]]</p>
//       <p>Parking : [[parking_status]]</p>
//       <p>Address : [[address]]</p>
//       <p>Notes : [[notes]]</p>
//       <p>Contact Status : [[contact_status]]</p>
//       <br/>
//       <p>Your appointment is tentative and you will be notified as we will confirm this booking.</p>
//       <br/>
//       <p>Thank you</p>
//     </div>
//   `;

//   const handleTextClick = (section, text) => {
//     const newText = `${textareaContent[section] || ""} ${text}`;
//     setTextareaContent((prev) => ({
//       ...prev,
//       [section]: newText,
//     }));
//     setPreviewContent(newText); // Update preview instantly
//   };

//   const handleDefaultTemplate = (section) => {
//     // If the default template is used, don't add it again
//     if (defaultTemplateUsed) return;
//     setTextareaContent((prev) => ({
//       ...prev,
//       [section]: defaultPreviewContent,
//     }));
//     setPreviewContent(defaultPreviewContent);  // Show default template in preview
//     setDefaultTemplateUsed(true); // Mark default template as used
//   };

//   const handleSaveTemplate = (section) => {
//     alert(`Template for section "${section}" has been saved.`);
//   };

//   const handlePreview = (section) => {
//     const modifiedContent = textareaContent[section] || defaultPreviewContent;
//     setPreviewContent(modifiedContent);  // Set modified content for preview
//     setPreviewTitle(section); // Set the title dynamically
//     setIsPreviewOpen(true);
//   };

//   const toggleSection = (section) => {
//     setExpandedSections((prev) => {
//       const newExpandedSections = { ...prev };
//       if (newExpandedSections[section]) {
//         newExpandedSections[section] = false;
//       } else {
//         Object.keys(newExpandedSections).forEach((key) => {
//           newExpandedSections[key] = false;
//         });
//         newExpandedSections[section] = true;
//       }
//       return newExpandedSections;
//     });
//   };

//   const clientSections = [
//     "Appointment Request",
//     "appointment_approved",
//     "appointment_rejected",
//     "appointment_cancelled_by_you",
//     "appointment_rescheduled_by_you",
//     "client_appointment_reminder",
//     "appointment_completed",
//   ];

//   const adminSections = [
//     "New Appointment Request Requires Approval",
//     "Appointment Approved",
//     "Appointment Rejected",
//     "Appointment Cancelled By Customer",
//     "Appointment Rescheduled By Customer",
//     "Admin Appointment Reminder",
//     "Appointment Completed",
//   ];

//   const staffSections = [
//     "New Appointment Assigned",
//     "Appointment Approved",
//     "Appointment Rejected",
//     "Appointment Cancelled By Customer",
//     "Appointment Rescheduled By Customer",
//     "Admin Appointment Reminder",
//     "Appointment Completed",
//   ];

//   const renderSections = (sections) =>
//     sections.map((section) => (
//       <div key={section}>
//         <div className="flex justify-between items-center bg-gray-100 p-4 rounded-md shadow-md xl:px-24">
//           <span
//             className="font-semibold text-lg break-words w-full"
//             style={{ wordBreak: "break-word" }}
//           >
//             {section}
//           </span>
//           <button
//             onClick={() => toggleSection(section)}
//             className={`relative w-16 h-6 rounded-full transition-all duration-500 flex items-center justify-start ${expandedSections[section] ? "bg-green-500 text-white" : "bg-gray-300 text-gray-600"
//               }`}
//             style={{
//               width: '3.5rem', // Increased width of the toggle button
//               height: '1.5rem', // Keep the height fixed
//             }}
//           >
//             <div
//               className={`w-6 h-6 bg-white rounded-full absolute transition-all ${expandedSections[section] ? "right-0.5" : "left-0.5"
//                 }`}
//             />
//           </button>
//         </div>

//         <div
//           className={`p-4 bg-gray-50 border rounded-lg shadow-md transform transition-all duration-1000 ease-in-out overflow-hidden ${expandedSections[section] ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
//             }`}
//         >
//           <div className="flex flex-col sm:flex-row gap-4">
//             <div className="flex-1 sm:w-4/5">
//               <textarea
//                 className="w-full h-64 p-2 border rounded-lg"
//                 placeholder="Enter your text here..."
//                 value={textareaContent[section] || ""}
//                 onChange={(e) => {
//                   setTextareaContent((prev) => ({
//                     ...prev,
//                     [section]: e.target.value,
//                   }));
//                   setPreviewContent(e.target.value); // Update preview instantly
//                 }}
//               />
//             </div>

//             <div className="w-full sm:w-1/5 flex flex-col gap-2">
//               <div className="flex-1 overflow-y-auto max-h-60">
//                 {[
//                   "Booking Date",
//                   "booking_time",
//                   "Service Name",
//                   "Units",
//                   "Addon",
//                   "firstname",
//                   "lastname",
//                   "client_email",
//                   "client_phone",
//                 ].map((text) => (
//                   <div
//                     key={text}
//                     className="text-sm text-gray-700 cursor-pointer hover:bg-gray-100 p-2 rounded"
//                     onClick={() => handleTextClick(section, text)}
//                   >
//                     {text}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>

//           <div className="flex gap-4 mt-4">
//             <button
//               className="px-4 py-2 bg-green-500 text-white rounded-lg shadow hover:bg-green-600"
//               onClick={() => handleSaveTemplate(section)}
//             >
//               Save Template
//             </button>
//             <button
//               className="px-4 py-2 bg-yellow-500 text-white rounded-lg shadow hover:bg-yellow-600"
//               onClick={() => handleDefaultTemplate(section)}
//             >
//               Default Template
//             </button>
//             <button
//               className="px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600"
//               onClick={() => handlePreview(section)}
//             >
//               Preview
//             </button>
//           </div>
//         </div>
//       </div>
//     ));

//   return (
//     <div className="container mx-auto mt-8  sm:px-2 lg:px-8 bg-white xl:px-52">
//       <div className="flex flex-nowrap justify-between border-b sm:border-b-4 md:border-b-8 border-gray-300 pb-4 mb-6 overflow-x-auto">
//         <div className="flex flex-row w-full gap-2">
//           {["Client Email Template", "Admin Email Template", "Staff Email Template"].map((tab) => (
//             <button
//               key={tab}
//               className={`flex-1 text-center px-2 sm:px-4 py-2 text-nowrap text-xs sm:text-sm font-medium rounded-lg transition-colors duration-300 ease-in-out ${activeTab === tab
//                 ? "bg-blue-500 border-b-4 border-blue-700 text-white"
//                 : "bg-transparent text-gray-700 hover:bg-blue-100"
//                 }`}
//               onClick={() => setActiveTab(tab)}
//             >
//               {tab}
//             </button>
//           ))}
//         </div>
//       </div>

//       {activeTab === "Client Email Template" && renderSections(clientSections)}
//       {activeTab === "Admin Email Template" && renderSections(adminSections)}
//       {activeTab === "Staff Email Template" && renderSections(staffSections)}

//       {/* Preview Modal */}
//       {isPreviewOpen && (
//         <div className="fixed top-0 left-0 z-50 w-full h-full bg-black bg-opacity-50 flex items-center justify-center">
//           <div className="bg-white w-full sm:w-4/5 md:w-3/5 lg:w-2/5 xl:w-1/3 h-[80%] p-8 rounded-lg shadow-lg overflow-hidden">
//             <div className="flex justify-between items-center ">
//               <h2 className="text-2xl font-bold">{previewTitle} Preview</h2>
//               <button
//                 onClick={() => setIsPreviewOpen(false)}
//                 className="text-lg font-semibold text-gray-600 hover:text-gray-900"
//               >
//                 X
//               </button>
//             </div>

//             {/* Scrollable Content Area */}
//             <div className="w-full h-full overflow-y-auto scrollbar-hide">
//               <label className="block text-lg font-semibold mb-2">Preview</label>
//               <div
//                 dangerouslySetInnerHTML={{ __html: previewContent }}
//                 className="prose max-w-none"
//               />
//               <div
//                 dangerouslySetInnerHTML={{ __html: defaultPreviewContent }}
//                 className="prose max-w-none"
//               />
//             </div>
//           </div>
//         </div>
//       )}


//     </div>
//   );
// };

// export default Email_Template;




